import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { FC, HTMLAttributes, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';

import RooftopsLoader from '../RooftopsLoader';
import {
  AmountContainerStyled,
  ClientContainerStyled,
  ClientsHeadStyled,
  ClientsListContainerStyled,
  ClientsTotalContainerStyled,
  ClientsWrapStyled,
  CounterStyled,
  CountersContainerStyled,
  CountersTotalStyled,
  IndentedClientsWrapStyled,
  ObserverSpanStyled,
  TextAmountStyled,
  TextClientsStyled,
  TextRightHeaderStyled,
  TextRooftTopNameStyled,
  TextTotalStyled,
} from './TicketsList.styled';
import Typography from 'components/Typography/Typography';
import { useTickets } from 'contexts/TicketsContext';
import { StorageE } from 'declarations/enums/storage';
import { ActiveTabTypeE } from 'declarations/enums/tab';
import { TicketsRefinementKeysE } from 'declarations/enums/tickets';
import { RoofTopI } from 'declarations/interfaces/roofTop';
import { formatAndRoundAmount } from 'helpers/locale';

type CounterPropsT = HTMLAttributes<HTMLParagraphElement> & {
  bgColor?: 'green' | 'orange' | 'red';
};

const TicketsList: FC<CounterPropsT> = () => {
  /**
   * Hooks
   */
  const {
    ticketsCountByRoofTop,
    getSearchParam,
    updateSearchParam,
    clearSearchParam,
    isLoadingTicketsCountByRoofTop,
    hasNextRooftopsPage,
    fetchNextRooftopsPage,
    shouldLoadMoreRooftops,
    mappedRooftopsList,
    dealerSelectedRoofTopCount,
    activeTab,
  } = useTickets();

  /**
   * Variables
   */
  const { ref, inView } = useInView();
  const lastRooftopObserver = ref;
  const isRoofTopIdFilterCleared = !getSearchParam(TicketsRefinementKeysE.RoofTopId);
  const countPerPage = 25;
  const isScheduleTab = activeTab === ActiveTabTypeE.Schedule;

  const savedRoofTops: RoofTopI[] = JSON.parse(
    localStorage.getItem(StorageE.SavedRoofTops) || '[]'
  );
  const hasSavedRoofTops = savedRoofTops.length > 0;

  const isLoaderObserverVisible =
    mappedRooftopsList?.length &&
    mappedRooftopsList.length >= countPerPage &&
    (!hasSavedRoofTops ||
      dealerSelectedRoofTopCount === 0 ||
      dealerSelectedRoofTopCount > countPerPage);

  /**
   * Handlers
   */
  const getColor = (quantity: number) => {
    if (quantity === 0) return 'green';
    if (quantity <= 10) return 'orange';
    if (quantity > 10) return 'red';
  };
  const handleUpdateFilterClick = (key: TicketsRefinementKeysE, value: string) => () =>
    updateSearchParam(key, value);
  const handleClearFilterClick = (key: TicketsRefinementKeysE) => () => clearSearchParam(key);

  /**
   * Effects
   */
  useEffect(() => {
    if (
      inView &&
      hasNextRooftopsPage &&
      mappedRooftopsList?.length &&
      mappedRooftopsList.length >= countPerPage &&
      dealerSelectedRoofTopCount > countPerPage &&
      mappedRooftopsList.length < dealerSelectedRoofTopCount
    ) {
      fetchNextRooftopsPage();
    }
  }, [
    inView,
    fetchNextRooftopsPage,
    hasNextRooftopsPage,
    mappedRooftopsList?.length,
    dealerSelectedRoofTopCount,
    countPerPage,
  ]);

  return (
    <ClientsWrapStyled className="roof-tops-list">
      <ClientsHeadStyled>
        <TextClientsStyled variant="h4" size="xl" color="semiTransparent">
          Clients
        </TextClientsStyled>
        {!isScheduleTab && (
          <TextAmountStyled variant="h4" size="xl" color="semiTransparent">
            Amount
          </TextAmountStyled>
        )}
        {!isScheduleTab && (
          <div>
            <TextRightHeaderStyled color="semiTransparent">Pending Actions</TextRightHeaderStyled>
            <CountersTotalStyled>
              <TextTotalStyled variant="body6" color="semiTransparent" align="center">
                Dealer
              </TextTotalStyled>
              <TextTotalStyled variant="body6" color="semiTransparent" align="center">
                WC
              </TextTotalStyled>
              <TextTotalStyled variant="body6" color="semiTransparent" align="center">
                OEM
              </TextTotalStyled>
            </CountersTotalStyled>
          </div>
        )}
      </ClientsHeadStyled>
      {isLoadingTicketsCountByRoofTop ? (
        <RooftopsLoader />
      ) : (
        <>
          <ClientsTotalContainerStyled
            isRoofTopIdFilterCleared={isRoofTopIdFilterCleared}
            onClick={handleClearFilterClick(TicketsRefinementKeysE.RoofTopId)}
            title="Filter by All"
          >
            <ClientContainerStyled color="light" $fullWidth={isScheduleTab}>
              <Typography color="light">All</Typography>
            </ClientContainerStyled>
            {!isScheduleTab && ticketsCountByRoofTop && ticketsCountByRoofTop !== null && (
              <AmountContainerStyled color="light">
                <Typography color="light" text-align="right">
                  {ticketsCountByRoofTop?.totalAmount > 9999999.99
                    ? `${formatAndRoundAmount(ticketsCountByRoofTop?.totalAmount).slice(0, 10)}...`
                    : formatAndRoundAmount(ticketsCountByRoofTop?.totalAmount)}
                </Typography>
              </AmountContainerStyled>
            )}
            {!isScheduleTab && (
              <CountersContainerStyled>
                <CounterStyled>{ticketsCountByRoofTop?.assignedToDealerCount}</CounterStyled>
                <CounterStyled>{ticketsCountByRoofTop?.assignedToWCCount}</CounterStyled>
                <CounterStyled>{ticketsCountByRoofTop?.assignedToOEMCount}</CounterStyled>
              </CountersContainerStyled>
            )}
          </ClientsTotalContainerStyled>
          <ClientsListContainerStyled>
            {mappedRooftopsList && mappedRooftopsList.length > 0 ? (
              mappedRooftopsList.map(ticket => {
                const roofTopIdFilter = getSearchParam(TicketsRefinementKeysE.RoofTopId);
                const isRoofTopSelected = !!roofTopIdFilter && roofTopIdFilter === ticket.roofTopId;

                return (
                  <IndentedClientsWrapStyled
                    data-testid="roof-top"
                    title={`Filter by ${ticket.roofTopName}`}
                    isRoofTopSelected={isRoofTopSelected}
                    key={ticket.roofTopId}
                    onClick={handleUpdateFilterClick(
                      TicketsRefinementKeysE.RoofTopId,
                      ticket.roofTopId
                    )}
                  >
                    <ClientContainerStyled color="light" $fullWidth={isScheduleTab}>
                      <TextRooftTopNameStyled color="light" $widerMaxWidth={isScheduleTab}>
                        {ticket.roofTopName}
                      </TextRooftTopNameStyled>
                    </ClientContainerStyled>
                    {!isScheduleTab && (
                      <AmountContainerStyled color="light">
                        <Typography color="light" text-align="right">
                          {ticket.amount > 9999999.99
                            ? `${formatAndRoundAmount(ticket.amount).slice(0, 10)}...`
                            : formatAndRoundAmount(ticket.amount)}
                        </Typography>
                      </AmountContainerStyled>
                    )}
                    {!isScheduleTab && (
                      <CountersContainerStyled>
                        <CounterStyled bgColor={getColor(ticket.assignedToDealerCount)}>
                          {ticket.assignedToDealerCount}
                        </CounterStyled>
                        <CounterStyled bgColor={getColor(ticket.assignedToWCCount)}>
                          {ticket.assignedToWCCount}
                        </CounterStyled>
                        <CounterStyled bgColor={getColor(ticket.assignedToDealerCount)}>
                          {ticket.assignedToOEMCount}
                        </CounterStyled>
                      </CountersContainerStyled>
                    )}
                  </IndentedClientsWrapStyled>
                );
              })
            ) : (
              <IndentedClientsWrapStyled isRoofTopSelected={false}>
                <Typography variant="body1" color="semiTransparent">
                  No dealerships available
                </Typography>
              </IndentedClientsWrapStyled>
            )}
            {isLoaderObserverVisible ? (
              <ObserverSpanStyled ref={lastRooftopObserver}>Load more</ObserverSpanStyled>
            ) : null}
            {shouldLoadMoreRooftops && isLoaderObserverVisible && (
              <Box sx={{ display: 'flex', justifyContent: 'center', margin: '30px' }}>
                <CircularProgress color="secondary" />
              </Box>
            )}
          </ClientsListContainerStyled>
        </>
      )}
    </ClientsWrapStyled>
  );
};

export default TicketsList;
